import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  CheckCircle2,
  FileText,
  HeartPulse,
  MapPin,
  Phone,
  Users,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import {
  formatWatDate,
  getDependantName,
  getVisitAddress,
  getVisitDependantName,
  getVisitPhone,
  getVisitStart,
  sortVisitsNewestFirst,
} from '../../lib/visitUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const mergeSnapshotDependant = (visit, dependant) => {
  if (dependant) return dependant;
  const snapshot = visit?.dependantSnapshot || {};
  return {
    id: visit?.dependantId || snapshot.id,
    firstName: snapshot.firstName || snapshot.first_name,
    lastName: snapshot.lastName || snapshot.last_name,
    name: snapshot.name || visit?.dependantName,
    phone: snapshot.phone,
    gender: snapshot.gender,
    relationship: snapshot.relationship,
    address: snapshot.address,
    mobilityStatus: snapshot.mobilityStatus,
    cognitiveStatus: snapshot.cognitiveStatus,
    knownConditions: snapshot.knownConditions || snapshot.known_conditions,
    emergencyContact: snapshot.emergencyContact || snapshot.emergency_contact,
  };
};

const DetailRow = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">{label}</p>
    <p className="mt-1 text-sm text-[#1A1A1A]">{value || '-'}</p>
  </div>
);

const AssignmentsPage = () => {
  const navigate = useNavigate();
  const { currentUser, dependants, subscribers, visits, loading, dataError } = useApp();
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const officerId = currentUser?.id || currentUser?.profileId;

  const assignments = useMemo(() => {
    const byDependant = new Map();
    visits
      .filter((visit) => (!officerId || visit.careOfficerId === officerId) && visit.status !== 'Cancelled' && visit.dependantId)
      .forEach((visit) => {
        const existing = byDependant.get(visit.dependantId) || { visits: [] };
        existing.visits.push(visit);
        byDependant.set(visit.dependantId, existing);
      });

    dependants
      .filter((dependant) => !officerId || dependant.assignedCareOfficer === officerId || byDependant.has(dependant.id))
      .forEach((dependant) => {
        const existing = byDependant.get(dependant.id) || { visits: [] };
        existing.dependant = dependant;
        byDependant.set(dependant.id, existing);
      });

    return Array.from(byDependant.entries()).map(([dependantId, item]) => {
      const sorted = sortVisitsNewestFirst(item.visits || []);
      const latestVisit = sorted[0];
      const dependant = mergeSnapshotDependant(latestVisit, item.dependant);
      const subscriber = subscribers.find((entry) => entry.id === latestVisit?.subscriberId || entry.id === dependant?.subscriberId);
      return {
        dependantId,
        dependant,
        subscriber,
        visits: sorted,
        latestVisit,
      };
    }).sort((a, b) => getDependantName(a.dependant).localeCompare(getDependantName(b.dependant)));
  }, [dependants, officerId, subscribers, visits]);

  const selectedDependant = selectedAssignment?.dependant;
  const selectedConditions = selectedDependant?.knownConditions || selectedDependant?.known_conditions || [];
  const selectedEmergency = selectedDependant?.emergencyContact || selectedDependant?.emergency_contact || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EE] px-4 py-10" data-testid="assignments-page">
        <div className="mx-auto max-w-7xl rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-8 text-[#5A5A5A]">Loading assignments...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="assignments-page">
      <div className="border-b border-[#E8E6E3] bg-[#FAFAF7]">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="font-serif text-2xl font-semibold text-[#1A1A1A] sm:text-3xl">My Assignments</h1>
              <p className="mt-1 text-sm text-[#5A5A5A]">Dependants you have cared for through approved visit schedules.</p>
            </div>
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-[#F5E8EA] px-3 py-1.5 text-sm font-medium text-[#9F1D35]">
              <Users className="h-4 w-4" />
              {assignments.length} assignments
            </span>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8">
        {dataError && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {dataError}
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {assignments.map((assignment) => {
            const { dependant, subscriber, latestVisit, visits: dependantVisits, dependantId } = assignment;
            const name = getVisitDependantName(latestVisit, dependant);
            const subscriberName = subscriber ? `${subscriber.firstName || ''} ${subscriber.lastName || ''}`.trim() : latestVisit?.subscriberSnapshot?.name || 'Unavailable';

            return (
              <article key={dependantId} className="overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
                <button
                  type="button"
                  className="block w-full p-4 text-left transition-colors hover:bg-[#F5E8EA]/50 sm:p-6"
                  onClick={() => setSelectedAssignment(assignment)}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-[#9F1D35] text-lg font-semibold text-[#FAFAF7]">
                      {name.split(' ').map((part) => part[0]).join('').slice(0, 2)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-lg font-semibold text-[#1A1A1A]">{name}</h3>
                      <p className="text-sm text-[#5A5A5A]">
                        {dependant?.relationship || 'Dependant'}{dependant?.age ? ` · ${dependant.age} years old` : ''}
                      </p>
                      <p className="mt-1 text-sm text-[#5A5A5A]">Subscriber: {subscriberName}</p>
                    </div>
                    <span className="rounded-full bg-[#F5E8EA] px-3 py-1 text-xs font-medium text-[#9F1D35]">
                      {dependantVisits.length} visits
                    </span>
                  </div>

                  <div className="mt-5 space-y-3">
                    <p className="flex items-start gap-2 text-sm text-[#5A5A5A]">
                      <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      {getVisitAddress(latestVisit, dependant)}
                    </p>
                    <p className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                      <Phone className="h-4 w-4" />
                      {getVisitPhone(latestVisit, dependant)}
                    </p>
                    <p className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                      <Calendar className="h-4 w-4" />
                      Last visit: {formatWatDate(getVisitStart(latestVisit))}
                    </p>
                  </div>
                </button>
              </article>
            );
          })}
        </div>

        {assignments.length === 0 && (
          <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-10 text-center">
            <CheckCircle2 className="mx-auto mb-4 h-16 w-16 text-emerald-500" />
            <h3 className="mb-2 text-lg font-semibold text-[#1A1A1A]">No assignments yet</h3>
            <p className="text-[#5A5A5A]">Once a visit is assigned to you, that dependant will appear here.</p>
          </div>
        )}
      </main>

      <Dialog open={Boolean(selectedAssignment)} onOpenChange={(open) => !open && setSelectedAssignment(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto border-[#E8E6E3] bg-[#FAFAF7] shadow-none sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1A1A1A]">{getDependantName(selectedDependant)}</DialogTitle>
            <DialogDescription>Registration details and care context.</DialogDescription>
          </DialogHeader>

          {selectedAssignment && (
            <div className="space-y-6">
              <div className="grid gap-4 rounded-2xl border border-[#E8E6E3] p-4 sm:grid-cols-2">
                <DetailRow label="Phone" value={getVisitPhone(selectedAssignment.latestVisit, selectedDependant)} />
                <DetailRow label="Gender" value={selectedDependant?.gender} />
                <DetailRow label="Relationship" value={selectedDependant?.relationship} />
                <DetailRow label="Mobility" value={selectedDependant?.mobilityStatus || selectedDependant?.mobility_status} />
                <DetailRow label="Cognitive Status" value={selectedDependant?.cognitiveStatus || selectedDependant?.cognitive_status} />
                <DetailRow label="Emergency Contact" value={[selectedEmergency.name, selectedEmergency.phone].filter(Boolean).join(' · ')} />
              </div>

              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Address</p>
                <p className="rounded-2xl border border-[#E8E6E3] p-4 text-sm text-[#1A1A1A]">
                  {getVisitAddress(selectedAssignment.latestVisit, selectedDependant)}
                </p>
              </div>

              <div>
                <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">
                  <HeartPulse className="h-4 w-4" />
                  Conditions
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedConditions.length > 0 ? selectedConditions.map((condition) => (
                    <span key={condition} className="rounded-full bg-[#F5E8EA] px-3 py-1 text-xs font-medium text-[#9F1D35]">{condition}</span>
                  )) : <span className="text-sm text-[#5A5A5A]">No conditions recorded.</span>}
                </div>
              </div>

              <button
                type="button"
                onClick={() => navigate(`/visits?dependant=${selectedAssignment.dependantId}`)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] hover:bg-[#7A1628]"
              >
                <FileText className="h-4 w-4" />
                View Visits
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssignmentsPage;
